import type { ReactNode } from "react";
import { cn } from "@shared/lib/utils";

/**
 * §1.3 color=meaning dictionary — single fixed mapping across every screen:
 * green = ok/gained/up, amber = pending/warn, red = error/fail ONLY,
 * neutral = closed/idle. No new hues for categorization.
 */
export type StatusTone = "positive" | "warning" | "danger" | "neutral";

const TONE_TEXT: Record<StatusTone, string> = {
  positive: "text-accent",
  warning: "text-yellow",
  danger: "text-danger",
  neutral: "text-ink-dim",
};

const TONE_DOT: Record<StatusTone, string> = {
  positive: "bg-accent",
  warning: "bg-yellow",
  danger: "bg-danger",
  neutral: "bg-ink-mute",
};

/**
 * Status pill — surface+1 background, 12px, 1–2 word noun label only
 * (`Active`, `Burned`); never prose (`Status: done` ×). One badge per row.
 */
export function StatusBadge({
  tone = "neutral",
  children,
  className,
}: {
  tone?: StatusTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-line bg-white/6 px-2.5 py-0.5 text-xs font-medium",
        TONE_TEXT[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Lifecycle-only dot (withdrawal processing, settlement…): pulses while
 * `active` (in progress), static once settled. Everything else is a badge.
 * Pulse collapses under prefers-reduced-motion via the global base.css rule.
 */
export function StatusDot({
  tone = "neutral",
  active = false,
  className,
}: {
  tone?: StatusTone;
  active?: boolean;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block size-1.5 shrink-0 rounded-full",
        TONE_DOT[tone],
        active && "animate-pulse",
        className,
      )}
    />
  );
}
